import React from 'react'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { keyframes } from '@mui/system'

export interface PrepDashboardPlaceholderItemProps {
  id: number
  slotText?: string | null
  onRefresh: () => void
  refreshing?: boolean
  disabled?: boolean
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const PrepDashboardPlaceholderItem: React.FC<PrepDashboardPlaceholderItemProps> = ({
  id,
  slotText,
  onRefresh,
  refreshing = false,
  disabled = false,
}) => {
  const fallback = React.useMemo(
    () => slotText?.trim() || `Prep item #${id} is unavailable right now.`,
    [id, slotText],
  )

  const handleClick = React.useCallback(() => {
    if (refreshing || disabled) {
      return
    }
    onRefresh()
  }, [disabled, onRefresh, refreshing])

  const refreshLabel = refreshing ? 'Refreshing prep item…' : 'Retry loading this prep item'

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'nowrap',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ flex: '1 1 auto', minWidth: 0, lineHeight: 1.5 }}
      >
        {fallback}
      </Typography>
      <Tooltip title={refreshLabel} enterDelay={300} disableInteractive>
        <span>
          <IconButton
            size="small"
            color="primary"
            aria-label={refreshLabel}
            onClick={handleClick}
            disabled={refreshing || disabled}
            sx={{
              ml: 'auto',
              ...(refreshing
                ? {
                    '& .MuiSvgIcon-root': {
                      animation: `${spin} 1s linear infinite`,
                    },
                  }
                : null),
            }}
          >
            <AutorenewIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

export default PrepDashboardPlaceholderItem
